"use client";

import { useState } from "react";
import { Popover, Stack, Typography } from "@mui/material";
import useTopLeads from "@lib/leads/useTopLeads";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell, EmptyState, Button } from "@/ui";
import dayjs from "dayjs";
import styles from "./TopLeads.module.css";

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7L12 13L2 7" />
    </svg>
  );
}


function buildInteractionText(stats) {
  const parts = [];
  if (stats.click_count > 0) {
    parts.push(`${stats.click_count} click${stats.click_count !== 1 ? "s" : ""}`);
  }
  if (stats.view_count > 0) {
    let viewText = `${stats.view_count} view${stats.view_count !== 1 ? "s" : ""}`;
    if (stats.total_view_duration > 0) {
      const seconds = stats.total_view_duration;
      const formatted = seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
      viewText += ` (${formatted})`;
    }
    parts.push(viewText);
  }
  return parts.join(", ");
}

export default function TopLeads({ changeRoute }) {
  const { leads, isLoading } = useTopLeads();
  const [phoneAnchorEl, setPhoneAnchorEl] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  if (isLoading) return null;

  const handlePhoneClick = (event, phone) => {
    setPhoneAnchorEl(event.currentTarget);
    setPhoneNumber(phone);
  };

  const handlePhoneClose = () => {
    setPhoneAnchorEl(null);
    setPhoneNumber("");
  };

  if (!leads?.length) {
    return (
      <Stack spacing={2} sx={{ minWidth: 0, width: '100%' }}>
        <Typography variant="h2">Top leads</Typography>
        <EmptyState
          title="No leads identified yet"
          description="Send a float to your contacts to start tracking engagement and surface your top leads."
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => changeRoute("/floats/create")}
            >
              Create a float
            </Button>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0, width: '100%' }}>
      <Typography variant="h2">Top leads</Typography>
      <Table hoverable>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Float</TableHeader>
            <TableHeader>Interactions</TableHeader>
            <TableHeader>Last seen</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {leads.map((lead, idx) => (
            <TableRow key={`${lead.contact.id}-${lead.float.id}-${idx}`}>
              <TableCell>
                <div style={{ fontWeight: 600 }}>{lead.contact.name}</div>
                {lead.contact.company && (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{lead.contact.company}</div>
                )}
              </TableCell>
              <TableCell>
                <a
                  href={`/floats/${lead.float.uuid}`}
                  onClick={(e) => { e.preventDefault(); changeRoute(`/floats/${lead.float.uuid}`); }}
                  style={{ color: "#13319A", textDecoration: "none" }}
                >
                  {lead.float.name}
                </a>
              </TableCell>
              <TableCell>{buildInteractionText(lead.stats)}</TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>
                {dayjs(lead.stats.latest_interaction_at).format("DD MMM YYYY")}
              </TableCell>
              <TableCell>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {lead.contact.linkedin_url ? (
                    <a href={lead.contact.linkedin_url} target="_blank" rel="noopener noreferrer" className={styles.actionBtn} title="LinkedIn">
                      <LinkedInIcon />
                    </a>
                  ) : (
                    <span className={styles.actionBtnDisabled}><LinkedInIcon /></span>
                  )}
                  {lead.contact.phone ? (
                    <button className={styles.actionBtn} onClick={(e) => handlePhoneClick(e, lead.contact.phone)} title="Phone">
                      <PhoneIcon />
                    </button>
                  ) : (
                    <span className={styles.actionBtnDisabled}><PhoneIcon /></span>
                  )}
                  {lead.contact.email ? (
                    <a href={`mailto:${lead.contact.email}`} className={styles.actionBtn} title="Email">
                      <EmailIcon />
                    </a>
                  ) : (
                    <span className={styles.actionBtnDisabled}><EmailIcon /></span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Popover
        open={Boolean(phoneAnchorEl)}
        anchorEl={phoneAnchorEl}
        onClose={handlePhoneClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Typography sx={{ p: 1.5 }}>Call {phoneNumber}</Typography>
      </Popover>
    </Stack>
  );
}
